import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormControl,
  Validators,
  AbstractControl,
  ValidatorFn,
} from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { IProperty } from '@shared/models/properties';

import { Store } from '@ngrx/store';
import { AppState } from '@state/app.state';
import { createOfferAction } from '@state/offers/offers.actions';
import {
  selectOffersCreateLoading,
  selectOffersCreateSuccess,
} from '@state/offers/offers.selectors';

@UntilDestroy()
@Component({
  selector: 'app-shared-offer-modal',
  templateUrl: './offer-modal.component.html',
  styleUrls: ['./offer-modal.component.scss'],
})
export class OfferModalComponent implements OnInit, OnChanges {
  @Input({ required: true }) open = false;
  @Output() modalClose = new EventEmitter<void>();
  @Output() successOfferSent = new EventEmitter<void>();
  @Input({ required: true }) property?: IProperty;

  protected loading = false;
  protected focused = '';

  public offerForm: FormGroup = this.formBuilder.group({
    price: new FormControl('', [Validators.required]),
    // type: new FormControl('', [Validators.required]),
    financingType: new FormControl('', [Validators.required]),
    isShowing: new FormControl('', [Validators.required]),
  });

  constructor(
    private formBuilder: FormBuilder,
    private store: Store<AppState>
  ) {
    this.offerForm
      .get('price')
      ?.setValidators([
        this.createMinMaxValidatorForPrice(this.offerForm.get('price')),
      ]);
  }

  ngOnInit(): void {
    this.store
      .select(selectOffersCreateLoading)
      .pipe(untilDestroyed(this))
      .subscribe((loading) => {
        this.loading = !!loading;
        if (loading) {
          this.offerForm.disable();
        } else {
          this.offerForm.enable();
        }
      });

    this.store
      .select(selectOffersCreateSuccess)
      .pipe(untilDestroyed(this))
      .subscribe((success) => {
        if (success) {
          this.onCloseHandler();
          setTimeout(() => {
            this.successOfferSent.emit();
          }, 0);
        }
      });

    this.setDefaultValues();
  }

  ngOnChanges(): void {
    this.setDefaultValues();
  }

  setDefaultValues(): void {
    if (this.open || this.property) {
      this.offerForm.patchValue({
        price: '',
        // type: '',
        financingType: '',
        isShowing: '',
      });
      this.offerForm.markAsUntouched();
    }
  }

  onCloseHandler(): void {
    this.modalClose.emit();
  }

  submitHandler(): void {
    if (this.offerForm.invalid || !this.property) {
      this.offerForm.markAllAsTouched();
      return;
    }

    try {
      const values = this.offerForm.value;
      this.store.dispatch(
        createOfferAction({
          input: {
            ...values,
            propertyId: this.property.id,
            isShowing: values.isShowing === 'YES',
          },
        })
      );
    } catch (err) {
      throw err;
    }
  }

  createMinMaxValidatorForPrice(control: AbstractControl | null): ValidatorFn {
    return () => {
      if (control?.value < 1000 || control?.value > 10000000) {
        return { custom: 'Offer should be between 1,000 and 10,000,000' };
      }

      return null;
    };
  }
}
